<template>
  <component :is="props.as || 'div'" :class="computedClasses">
    <div v-if="hasMediaSlot" :class="$style.media">
      <slot name="media" />
    </div>
    <div
      v-if="hasHeaderSlot || hasSubHeaderSlot || hasBodySlot || hasActionsSlot"
      expand
      :class="[$style.content, contentClass]"
    >
      <header v-if="hasHeaderSlot" :class="$style.header">
        <slot name="header" />
      </header>
      <div v-if="hasSubHeaderSlot" :class="$style.subheader">
        <slot name="subheader" />
      </div>
      <div v-if="hasBodySlot" :class="$style.body">
        <slot name="body" />
      </div>
      <footer v-if="hasActionsSlot" :class="$style.actions">
        <slot name="actions" />
      </footer>
    </div>
  </component>
</template>

<script setup lang="ts">
const $style = useCssModule()
const slots = useSlots()

const props = defineProps<{
  as?: string
  alignment?: 'left' | 'center' | 'right'
  contentClass?: string
}>()

const computedClasses = computed(() => {
  return {
    [$style.config]: true,
    [$style.card]: true,
    [$style.alignCenter]: props.alignment === 'center',
    [$style.alignLeft]: props.alignment === 'left',
    [$style.alignRight]: props.alignment === 'right',
  }
})

/* eslint-disable @typescript-eslint/no-explicit-any */
// eslint is disable here since we don't know exactly
// what data is passed in the slots
const hasMediaSlot = computed<any>(() => !!slots.media)
const hasHeaderSlot = computed<any>(() => !!slots.header)
const hasSubHeaderSlot = computed<any>(() => !!slots.subheader)
const hasBodySlot = computed<any>(() => !!slots.body)
const hasActionsSlot = computed<any>(() => !!slots.actions)
/* eslint-enable @typescript-eslint/no-explicit-any */
</script>

<style lang="scss" module>
.config {
  --card-background: var(--white);
  --card-color: #222;
  --content-padding: 1em;

  &.alignCenter {
    text-align: center;
  }

  &.alignLeft {
    text-align: left;
  }

  &.alignRight {
    text-align: right;
  }

  @include media('md') {
    --content-padding: 1em 2em;
  }
}

.card {
  background: var(--card-background);
  color: var(--card-color);
  display: flex;
  height: 100%;
  flex-direction: column;
}

.media,
.imgSrc {
  width: 100%;
}

.content {
  display: flex;
  height: 100%;
  flex-direction: column;

  padding: var(--content-padding);
}

.header {
  font-size: 1.25em;
  line-height: 1.25;
  margin: 0.5em 0 0.75em;
  width: 100%;
}

.subheader {
  width: 100%;
}

.body {
  flex: 1 0 auto;
  width: 100%;
  line-height: math.div(30, 19);
  @include fluid-type(19px, 19px);

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: var(--roboto-condensed);
    line-height: math.div(30, 25);
    margin-bottom: 0.5em;
    @include fluid-type(18px, 25px);
  }
}

.actions {
  margin: auto;
  width: 100%;
  padding: 1.25em 0 0.5em;
}
</style>
